import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from './modals/ChangePasswordModal';
import Lottie from 'lottie-react';

const Header = ({ onToggleSidebar, sidebarCollapsed, sidebarVisible, onLogout }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [shopName, setShopName] = useState('');
  const [userMobile, setUserMobile] = useState('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [walletAnimationData, setWalletAnimationData] = useState(null);

  const navigate = useNavigate();
  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    window.addEventListener('resize', handleResize);
    
    // Get user information from localStorage
    const role = localStorage.getItem('UserRole');
    const shop = localStorage.getItem('ShopName');
    const mobile = localStorage.getItem('UserMobile');
    
    if (role) setUserRole(role);
    if (shop) setShopName(shop);
    if (mobile) setUserMobile(mobile);

    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const notifications = [];

  useEffect(() => {
    fetch('/FlyingWalletMoney.json')
      .then(response => response.json())
      .then(data => setWalletAnimationData(data))
      .catch(error => console.error('Error loading wallet animation:', error));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getToggleIcon = () => {
    if (isMobile) {
      return sidebarVisible ? 'bi-x-lg' : 'bi-list';
    }
    return sidebarCollapsed ? 'bi-list' : 'bi-chevron-left';
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    setShowProfileMenu(false);
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleNavigateToProfile = () => {
    setShowProfileMenu(false);
    navigate('/profile');
  };

  const handleProfileLinkClick = (e) => {
    e.preventDefault();
    handleNavigateToProfile();
  };

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    handleNavigateToProfile();
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setShowProfileMenu(false);
    setShowChangePasswordModal(true);
  };


  return (
    <header className={`dms-header ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="header-menu">
        <i 
          className={`bi ${getToggleIcon()} fs-responsive-md`}
          style={{ cursor: 'pointer' }}
          onClick={onToggleSidebar}
        ></i>
      </div>
      
      <div className="header-actions">

        {/* Notifications */}
        <div className="notification-container" ref={notificationRef}>
          <div className="notification-icon" onClick={toggleNotifications}>
            <i className="bi bi-bell fs-responsive-md"></i>
            <span className="notification-badge fs-responsive-xs">
              {notifications.filter(n => n.unread).length}
            </span>
          </div>
          
          {showNotifications && (
            <div className="notification-dropdown">
              <div className="dropdown-header">
                <h6>Notifications</h6>
                <span className="notification-count">
                  {notifications.filter(n => n.unread).length} new
                </span>
              </div>
              <div className="notification-list">
                {notifications.map((notification) => (
                  <div 
                    key={notification.id} 
                    className={`notification-item ${notification.unread ? 'unread' : ''}`}
                  >
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{notification.time}</div>
                    </div>
                    {notification.unread && <div className="unread-indicator"></div>}
                  </div>
                ))}
              </div>
              <div className="dropdown-footer">
                <a href="#" className="view-all-link">View all notifications</a>
              </div>
            </div>
          )}
        </div>

        {/* Profile Menu */}
        <div className="profile-container" ref={profileRef}>
          <div className="user-profile" onClick={toggleProfileMenu}>
            <div className="user-avatar" onClick={handleAvatarClick}>
              <i className="bi bi-person-circle fs-responsive-lg"></i>
            </div>
            <div className="user-info d-none d-md-block" onClick={handleAvatarClick}>
              <div className="user-name">{shopName || userRole}</div>
              <div className="user-role">{userMobile}</div>
            </div>
            <i className="bi bi-chevron-down profile-chevron d-none d-md-block"></i>
          </div>
          
          {showProfileMenu && (
            <div className="profile-dropdown">
              <div className="dropdown-header">
                <div className="profile-avatar">
                  <i className="bi bi-person-circle"></i>
                </div>
                <div className="profile-details">
                  <div className="profile-name">{shopName || userRole}</div>
                  <div className="profile-email">{userMobile}</div>
                </div>
              </div>
              <div className="profile-menu-list">
                
                <a href="#" className="profile-menu-item" onClick={handleChangePassword}>
                  <i className="bi bi-lock-fill"></i>
                  <span>Change Password</span>
                </a>
                <hr className="dropdown-divider" />
                <button onClick={handleLogout} className="profile-menu-item logout">
                  <i className="bi bi-box-arrow-right"></i>
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        show={showChangePasswordModal} 
        handleClose={() => setShowChangePasswordModal(false)} 
      />

    </header>
  );
};

export default Header;